import { filterAstToText, type FilterNode } from '@xphoto/shared';
import type {
  AssetSort,
  ExifTrackedField,
  PublishStateFilter,
  SortDir,
  TagMode,
} from '@xphoto/shared';
import type { GalleryFilters } from './api';

/**
 * Sélection du filtre « Type » de la barre d'outils. Distincte de l'enum stocké
 * `AssetKind` (`image`/`video`) : `animatedImage` est un SOUS-ENSEMBLE des images
 * (GIF/WebP/APNG animés) et `videoWithSound` un SOUS-ENSEMBLE des vidéos (celles
 * portant une piste audio, `kind=video` + `hasAudio=true`). `undefined`/absent ⇒
 * « Tous » (pas de filtre).
 */
export type MediaTypeFilter = 'image' | 'animatedImage' | 'video' | 'videoWithSound';

/**
 * État de requête de la galerie, dérivé en filtres API. Séparé des composants
 * pour être testable et pour que la construction de la requête soit explicite.
 */

/** Onglets de navigation (XPHOTO-18). */
export type GalleryTab = 'gallery' | 'folders' | 'tags';

export const GALLERY_TABS: readonly GalleryTab[] = ['gallery', 'folders', 'tags'];

/** État complet piloté par la barre d'outils + les onglets. */
export interface GalleryState {
  tab: GalleryTab;
  sort: AssetSort;
  dir: SortDir;
  /**
   * Graine de l'ordre ALÉATOIRE (`sort==='random'`) : rend la pagination et
   * l'aller-retour visionneuse cohérents (sinon chaque page se re-mélangerait). Elle
   * n'est PLUS sérialisée dans l'URL (LOT C1) — c'est la graine du COMPTE (persistée
   * serveur), injectée par `sort-resolution.ts` ; seules les URL LEGACY `?graine=` en
   * portent encore une. Ignorée hors `sort==='random'`.
   */
  seed?: string | undefined;
  /** Recherche libre sur le nom de fichier (`q`). */
  search: string;
  /** Dossier sélectionné (onglet Dossiers) — préfixe de `rel_path`. */
  folder?: string | undefined;
  /** Tag sélectionné (onglet Tags) — id ou label. */
  tag?: string | undefined;
  /** Champ EXIF filtré (lien profond Outil EXIF → carte d'analyse, XPHOTO-24). */
  hasExifField?: ExifTrackedField | undefined;
  /** État de publication filtré (lien profond Outil EXIF → panneau publication). */
  publishState?: PublishStateFilter | undefined;
  /**
   * Filtre par type de média (`image` statiques / `animatedImage` animées / `video`
   * / `videoWithSound` vidéos avec piste audio). `undefined`/absent ⇒ « Tous »
   * (défaut) — section « Type » du menu Trier & filtrer. Axe indépendant des onglets.
   */
  mediaType?: MediaTypeFilter | undefined;
  /**
   * Filtre « statué / non statué » du système « Taguer » : `false` = uniquement les
   * photos NON encore taguées (bascule « Non taguées » du menu Trier & filtrer → enchaîne avec
   * « Tout sélectionner » + « Réviser / valider les tags »), `true` = uniquement les taguées,
   * `undefined`/absent ⇒ pas de filtre. Axe indépendant des onglets.
   */
  tagged?: boolean | undefined;
  /**
   * Filtre « À valider (IA) » : uniquement les photos porteuses de propositions IA en
   * attente ET pas encore statuées (`tagged=false`). Plus étroit que « Non taguées »
   * (exclut les photos jamais touchées par l'IA). Mutuellement exclusif avec `tagged`
   * côté UI (bascules du menu Trier & filtrer). `undefined`/absent ⇒ pas de filtre.
   */
  pendingAiTags?: boolean | undefined;

  // --- Recherche avancée (panneau dédié) — 2 critères combinés en ET entre eux ET
  //     avec tout le reste (onglet/dossier/tag/type/recherche…). Toujours présents
  //     dans l'état (valeur « vide » explicite) ; le VRAI défaut de l'Accueil ne les
  //     touche pas (ce sont des filtres, pas un tri). Le filtre TYPE est porté par
  //     l'unique axe `mediaType` (menu Trier & filtrer), plus par la recherche.
  /**
   * Note MINIMALE (1..5) : n'inclure que les assets notés au moins ainsi. `null` =
   * aucun filtre de note (défaut). Le sélecteur d'étoiles à 0 = « toutes » ⇒ `null`.
   */
  ratingMin: number | null;
  /**
   * Tags cochés (MULTI, par id). Ensemble VIDE = aucun filtre tag. Combiné à `tagMode`.
   * Axe INDÉPENDANT du `tag` single (facette onglet Tags / lien profond) : peut viser
   * plusieurs tags à la fois avec la bascule ET/OU.
   */
  tagIds: string[];
  /** Mode de combinaison de `tagIds` : `all` (ET — porte tous) / `any` (OU — au moins un, défaut). */
  tagMode: TagMode;

  /**
   * FILTRE AVANCÉ — expression booléenne en TEXTE (mini-langage `filter-expr.ts` de
   * `@xphoto/shared`). Quand présent (non vide), il est transmis au backend via `?expr=`
   * (ANDé avec le reste par l'API) et sérialisé dans l'URL sous `?filtre=`. Posé par le
   * menu « Mes filtres » ou l'éditeur d'expression (`applySavedFilter` : état PROPRE, les
   * axes simples sont remis à zéro pour éviter la double-lecture). `undefined`/vide ⇒ pas
   * de filtre avancé.
   */
  expr?: string | undefined;
}

/**
 * Tri DÉTERMINISTE historique (date de prise de vue décroissante). Sert de base aux
 * vues où l'aléatoire n'a pas de sens (liens profonds EXIF vers un sous-ensemble
 * filtré : on veut un ordre stable et lisible, pas un mélange re-graine à chaque visite).
 */
export const DEFAULT_DETERMINISTIC_SORT: AssetSort = 'takenAt';
export const DEFAULT_DETERMINISTIC_DIR: SortDir = 'desc';

/**
 * État initial NEUTRE (tri déterministe date décroissante). Le VRAI défaut de l'Accueil
 * (quand aucun `tri` n'est dans l'URL) est la PRÉFÉRENCE utilisateur
 * (`gallery/default-sort-pref.ts`, localStorage) — souvent « aléatoire » — appliquée par
 * `sort-resolution.ts` avec la graine du COMPTE, pas figée ici, pour garder le module
 * `url-state` PUR (aucun accès storage). Un `tri=` explicite dans l'URL prime TOUJOURS.
 * Cette constante sert de repli neutre (module pur, tests, liens EXIF).
 */
export const INITIAL_GALLERY_STATE: GalleryState = {
  tab: 'gallery',
  sort: DEFAULT_DETERMINISTIC_SORT,
  dir: DEFAULT_DETERMINISTIC_DIR,
  search: '',
  ratingMin: null,
  tagIds: [],
  tagMode: 'any',
};

/**
 * Vrai si AU MOINS un critère de recherche avancée est actif (note min / tags cochés)
 * — pilote le surlignage du déclencheur et l'affichage des puces.
 */
export function hasAdvancedFilter(state: GalleryState): boolean {
  return (state.ratingMin !== null && state.ratingMin > 0) || state.tagIds.length > 0;
}

/**
 * Lien profond depuis l'Outil EXIF vers la galerie filtrée (XPHOTO-24) : soit un
 * champ EXIF (carte d'analyse), soit un état de publication (panneau publication).
 *
 * `folder` (optionnel) reporte la PORTÉE DOSSIER active dans l'outil → la galerie
 * ouverte est bornée à ce sous-arbre (`?dossier=<X>`), cohérente avec les compteurs
 * de l'analyse re-scopée. Absent ⇒ toute la bibliothèque.
 */
export type ExifDeepLink =
  | { kind: 'field'; field: ExifTrackedField; folder?: string }
  | { kind: 'publish'; state: PublishStateFilter; folder?: string };

/**
 * Applique un lien profond de l'Outil EXIF : pose le filtre EXIF correspondant, en
 * repartant d'un état PROPRE (recherche/tag remis à zéro) pour que le résultat
 * corresponde exactement au compteur affiché dans l'outil. Le tri courant est conservé.
 *
 * PORTÉE DOSSIER : si `link.folder` est fourni, on bascule sur l'onglet Dossiers et on
 * pose ce dossier (le filtre `folder` de la galerie n'est actif que sur cet onglet) →
 * la grille ouverte reflète le MÊME sous-arbre que l'analyse. Sinon, onglet Galerie.
 */
export function applyExifDeepLink(state: GalleryState, link: ExifDeepLink): GalleryState {
  const scoped = link.folder !== undefined && link.folder !== '';
  // Un lien profond cible un SOUS-ENSEMBLE filtré précis (les photos qui portent le
  // champ) : on veut un ordre STABLE et lisible, pas un mélange re-graine à chaque
  // visite. Si le tri courant est aléatoire, on retombe sur le tri déterministe.
  const sort = state.sort === 'random' ? DEFAULT_DETERMINISTIC_SORT : state.sort;
  const dir = state.sort === 'random' ? DEFAULT_DETERMINISTIC_DIR : state.dir;
  const base: GalleryState = {
    ...INITIAL_GALLERY_STATE,
    tab: scoped ? 'folders' : 'gallery',
    sort,
    dir,
    seed: undefined,
    ...(scoped ? { folder: link.folder } : {}),
  };
  return link.kind === 'field'
    ? { ...base, hasExifField: link.field }
    : { ...base, publishState: link.state };
}

/** Vrai si un filtre EXIF (lien profond) est actif — pilote la puce d'état. */
export function hasExifFilter(state: GalleryState): boolean {
  return state.hasExifField !== undefined || state.publishState !== undefined;
}

/** Vrai si un FILTRE AVANCÉ (expression booléenne) est actif — pilote les puces d'expression. */
export function hasExprFilter(state: GalleryState): boolean {
  return state.expr !== undefined && state.expr.trim() !== '';
}

/**
 * Applique un FILTRE AVANCÉ (depuis le menu « Mes filtres » ou l'éditeur d'expression),
 * façon `applyExifDeepLink` : repart d'un état PROPRE (`INITIAL_GALLERY_STATE`), pose
 * l'`expr` et CONSERVE le tri courant (retombe sur le tri déterministe si aléatoire — un
 * filtre cible un sous-ensemble précis, on veut un ordre stable et lisible). Les axes
 * SIMPLES (type/note/tags/recherche/dossier/tag/EXIF/tagged…) sont remis à zéro pour
 * éviter la double-lecture (le filtre avancé porte à lui seul toute la condition).
 */
export function applySavedFilter(state: GalleryState, exprText: string): GalleryState {
  const sort = state.sort === 'random' ? DEFAULT_DETERMINISTIC_SORT : state.sort;
  const dir = state.sort === 'random' ? DEFAULT_DETERMINISTIC_DIR : state.dir;
  return {
    ...INITIAL_GALLERY_STATE,
    sort,
    dir,
    seed: undefined,
    expr: exprText,
  };
}

/**
 * Construit un texte d'EXPRESSION à partir des axes SIMPLES actifs de l'état (type, note
 * minimale, tags cochés + mode ET/OU, tag/ dossier de facette, recherche par nom, statut
 * de tagage), combinés en ET, puis sérialisé en texte canonique via `filterAstToText`.
 *
 * Sert au « Enregistrer le filtre actuel » quand AUCUN `expr` n'est encore actif (sinon on
 * enregistre l'`expr` courant tel quel). Renvoie une chaîne VIDE si aucun axe simple n'est
 * actif (rien à enregistrer). Les ids de tags sont émis tels quels (`tag:<uuid>` — le
 * backend résout un tag par id OU par label).
 */
export function galleryStateToExprText(state: GalleryState): string {
  const nodes: FilterNode[] = [];
  if (state.mediaType) nodes.push({ kind: 'type', value: state.mediaType });
  if (state.ratingMin !== null && state.ratingMin > 0) {
    nodes.push({ kind: 'rating', op: 'gte', value: state.ratingMin });
  }
  if (state.tagIds.length > 0) {
    const tagNodes: FilterNode[] = state.tagIds.map((value) => ({ kind: 'tag', value }));
    nodes.push(
      tagNodes.length === 1
        ? tagNodes[0]!
        : { kind: state.tagMode === 'all' ? 'and' : 'or', children: tagNodes },
    );
  }
  if (state.tab === 'tags' && state.tag) nodes.push({ kind: 'tag', value: state.tag });
  if (state.tab === 'folders' && state.folder) nodes.push({ kind: 'folder', value: state.folder });
  const q = state.search.trim();
  if (q) nodes.push({ kind: 'filename', value: q });
  if (state.pendingAiTags) nodes.push({ kind: 'status', value: 'aiPending' });
  else if (state.tagged === true) nodes.push({ kind: 'status', value: 'tagged' });
  else if (state.tagged === false) nodes.push({ kind: 'status', value: 'untagged' });

  if (nodes.length === 0) return '';
  const ast: FilterNode = nodes.length === 1 ? nodes[0]! : { kind: 'and', children: nodes };
  return filterAstToText(ast);
}

/**
 * Traduit l'état d'UI en filtres API (`GalleryFilters`). Recherche vide → param omis.
 */
export function stateToFilters(state: GalleryState): GalleryFilters {
  const filters: GalleryFilters = {
    sort: state.sort,
    dir: state.dir,
  };
  // Ordre aléatoire : la graine (stable pour la session/URL) rend l'ordre reproductible
  // page après page (pagination + rang visionneuse cohérents). Ignorée hors random.
  if (state.sort === 'random' && state.seed) filters.seed = state.seed;
  const q = state.search.trim();
  if (q) filters.q = q;
  // Onglet Dossiers = navigation façon EXPLORATEUR : on ne montre que les photos
  // DIRECTEMENT dans le dossier courant (`folderDirect`), la racine (`folder=''`)
  // incluse — les sous-dossiers sont présentés à part sous forme de cartes.
  // EXCEPTION : un lien profond EXIF (champ/publication) borne un SOUS-ARBRE et doit
  // rester RÉCURSIF (cohérent avec les compteurs de l'outil) → on n'active PAS
  // `folderDirect` et on ne pose le dossier que s'il est défini.
  if (state.tab === 'folders') {
    if (hasExifFilter(state)) {
      if (state.folder) filters.folder = state.folder;
    } else {
      filters.folder = state.folder ?? '';
      filters.folderDirect = true;
    }
  }
  if (state.tab === 'tags' && state.tag) filters.tag = state.tag;
  // Filtres EXIF (lien profond depuis l'Outil EXIF) — axes indépendants des onglets.
  if (state.hasExifField) filters.hasExifField = state.hasExifField;
  if (state.publishState) filters.publishState = state.publishState;
  // Filtre « taguées / non taguées » (système « Taguer ») — axe indépendant. Absent ⇒
  // pas de filtre ; `false` = non taguées, `true` = taguées.
  if (state.tagged !== undefined) filters.tagged = state.tagged;
  // Filtre « À valider (IA) » — axe indépendant ; le prédicat backend implique déjà
  // `tagged=false` (pas besoin de le poser ici). Émis seulement si actif.
  if (state.pendingAiTags) filters.pendingAiTags = true;
  // Filtre Type — axe indépendant, compose avec l'onglet/dossier. « Tous » (absent)
  // ⇒ pas de filtre. Les images animées sont un SOUS-ENSEMBLE des images, et les
  // vidéos avec son un SOUS-ENSEMBLE des vidéos :
  //   image          → images STATIQUES (kind=image + animated=false)
  //   animatedImage  → images ANIMÉES  (kind=image + animated=true)
  //   video          → vidéos          (kind=video)
  //   videoWithSound → vidéos SONORES  (kind=video + hasAudio=true)
  if (state.mediaType === 'image') {
    filters.kind = 'image';
    filters.animated = false;
  } else if (state.mediaType === 'animatedImage') {
    filters.kind = 'image';
    filters.animated = true;
  } else if (state.mediaType === 'video') {
    filters.kind = 'video';
  } else if (state.mediaType === 'videoWithSound') {
    filters.kind = 'video';
    filters.hasAudio = true;
  }
  // Recherche avancée (panneau dédié) — émise SEULEMENT quand active (querystring propre) :
  //  - `ratingMin` : note minimale 1..5 (0/`null` = pas de filtre) ;
  //  - `tagIds` + `tagMode` : tags cochés + mode ET/OU (`tagMode` inutile sans tags).
  // Le filtre par TYPE de média n'a plus qu'UNE source (`mediaType` ci-dessus) → plus
  // de composition avec un `kinds` multi (retiré), donc plus de contradiction possible.
  if (state.ratingMin !== null && state.ratingMin > 0) filters.ratingMin = state.ratingMin;
  if (state.tagIds.length > 0) {
    filters.tagIds = [...state.tagIds];
    filters.tagMode = state.tagMode;
  }
  // Filtre AVANCÉ (expression booléenne) — transmis en `?expr=<texte>` (le client de
  // galerie sérialise automatiquement toutes les clés de `GalleryFilters`, dont `expr`).
  // Le backend le parse et l'AND avec le reste ; une expression VIDE est omise (URL propre).
  if (state.expr !== undefined && state.expr.trim() !== '') filters.expr = state.expr;
  return filters;
}
